/**
 * LivePublish Component
 * ======================
 * Phase 8B: Publish content to social platforms
 * With failure handling and retry capability
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Send, CheckCircle, XCircle, AlertTriangle, Loader2, ExternalLink,
  RefreshCcw, FileText, Link2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Publishing...', color: 'bg-blue-500', icon: Loader2 },
  success: { label: 'Published', color: 'bg-green-500', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-500', icon: XCircle },
  retrying: { label: 'Retrying...', color: 'bg-yellow-500', icon: RefreshCcw }
};

export const LivePublish = ({
  open,
  onClose,
  entityType = 'design',
  entityId,
  entityName,
  approvalStatus,
  onPublishSuccess
}) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);

  const getHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch connected accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/social/accounts`, { headers: getHeaders() });
        setAccounts(response.data.accounts || []);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchAccounts();
      setResult(null);
    }
  }, [open]);

  // Execute live publish
  const executePublish = async () => {
    if (!selectedAccount) {
      toast.error('Please select an account');
      return;
    }

    setPublishing(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/social/accounts/${selectedAccount}/publish`,
        {
          entity_type: entityType,
          entity_id: entityId,
          caption: caption || null,
          hashtags: hashtags ? hashtags.split(',').map(t => t.trim()) : null
        },
        { headers: getHeaders() }
      );

      setResult(response.data);

      if (response.data.success) {
        toast.success('Published successfully!');
        onPublishSuccess?.();
      } else {
        toast.error(`Publish failed: ${response.data.error_message || 'Unknown error'}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Publish failed';
      toast.error(detail);
      setResult({
        success: false,
        error_message: detail,
        can_retry: true
      });
    } finally {
      setPublishing(false);
    }
  };

  // Retry failed publish
  const retryPublish = async () => {
    if (!result?.log_id) return;

    setPublishing(true);
    try {
      const response = await axios.post(
        `${API}/social/logs/${result.log_id}/retry`,
        {},
        { headers: getHeaders() }
      );

      setResult(response.data);

      if (response.data.success) {
        toast.success('Retry successful!');
        onPublishSuccess?.();
      } else {
        toast.error(`Retry failed: ${response.data.error_message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Retry failed');
    } finally {
      setPublishing(false);
    }
  };

  const connectedAccounts = accounts.filter(a => a.status === 'connected');
  const selectedAccountData = connectedAccounts.find(a => a.id === selectedAccount);
  const statusConfig = result ? STATUS_CONFIG[result.status] || STATUS_CONFIG.pending : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-green-500" />
            Publish to Social Media
          </DialogTitle>
          <DialogDescription>
            Publish your approved content to connected social accounts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Entity Info */}
          <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <span className="font-medium dark:text-white">{entityName}</span>
              </div>
              <Badge variant={approvalStatus === 'approved' ? 'default' : 'secondary'} className="bg-green-500">
                {approvalStatus || 'not_submitted'}
              </Badge>
            </div>
          </div>

          {approvalStatus !== 'approved' && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-800 dark:text-red-300">
              <XCircle className="h-4 w-4 inline mr-2" />
              Content must be approved before publishing
            </div>
          )}

          {/* Account Selection */}
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div>
              <Label>Select Account</Label>
              {connectedAccounts.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-1">
                  No connected accounts. Connect an account in Social Settings first.
                </p>
              ) : (
                <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select an account to publish to" />
                  </SelectTrigger>
                  <SelectContent>
                    {connectedAccounts.map(account => (
                      <SelectItem key={account.id} value={account.id}>
                        <div className="flex items-center gap-2">
                          <span className="capitalize">{account.platform}</span>
                          <span className="text-muted-foreground">-</span>
                          <span>{account.account_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Caption Override */}
          <div>
            <Label>Caption (Optional Override)</Label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Leave empty to use design description"
              className="mt-1 min-h-[80px]"
              disabled={publishing}
            />
            {selectedAccountData?.platform === 'twitter' && caption.length > 280 && (
              <p className="text-xs text-red-500 mt-1">
                Twitter limit: 280 characters (current: {caption.length})
              </p>
            )}
          </div>

          {/* Hashtags */}
          <div>
            <Label>Hashtags (comma separated)</Label>
            <Textarea
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              placeholder="#marketing, #design, #social"
              className="mt-1 min-h-[50px]"
              disabled={publishing}
            />
          </div>

          {/* Result */}
          {result && (
            <Card className={result.success ? 'border-green-500' : 'border-red-500'}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  {statusConfig && (
                    <>
                      {React.createElement(statusConfig.icon, {
                        className: `h-4 w-4 ${result.success ? 'text-green-500' : result.status === 'retrying' ? 'text-yellow-500 animate-spin' : 'text-red-500'}`
                      })}
                      <span className={result.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                        {statusConfig.label}
                      </span>
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                {result.success && (
                  <>
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                      <CheckCircle className="h-4 w-4" />
                      <span>Your content has been published!</span>
                    </div>
                    {result.platform_post_id && (
                      <p className="text-xs text-muted-foreground">
                        Post ID: {result.platform_post_id}
                      </p>
                    )}
                    {result.platform_post_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(result.platform_post_url, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View Post
                      </Button>
                    )}
                  </>
                )}

                {!result.success && (
                  <>
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                      <XCircle className="h-4 w-4" />
                      <span>{result.error_message || 'Unknown error occurred'}</span>
                    </div>
                    {result.can_retry && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={retryPublish}
                        disabled={publishing}
                        className="mt-2"
                      >
                        {publishing ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <RefreshCcw className="h-3 w-3 mr-1" />
                        )}
                        Retry Publish
                      </Button>
                    )}
                  </>
                )}

                {result.log_id && (
                  <p className="text-xs text-muted-foreground">
                    Log ID: {result.log_id}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Publishing Progress */}
          {publishing && !result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                <span className="text-sm">Publishing to {selectedAccountData?.platform}...</span>
              </div>
              <Progress value={66} className="h-2" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={publishing}>
            {result?.success ? 'Done' : 'Cancel'}
          </Button>
          {!result?.success && (
            <Button
              onClick={executePublish}
              disabled={publishing || !selectedAccount || approvalStatus !== 'approved'}
              className="bg-green-600 hover:bg-green-700"
            >
              {publishing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Publish Now
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LivePublish;
